import React from "react";
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Divider,
  FormControlLabel,
  Grid,
  MenuItem,
  Paper,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { VisibilityOutlined } from "@mui/icons-material";
import { useLocation, useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import { getRequest, postRequest, putRequest } from "Includes/api/apicall";
import { GET_URL, POST_URL, PUT_URL } from "Includes/urls";

const emptyToggles = {
  erp: true,
  otherService: false,
  idCard: false,
  biometric: false,
};

const emptyForm = {
  name: "",
  description: "",
  amount_per_student: "",
  erp_with_gst: false,
  erp_gst_percent: "18",
  per_id_amount: "",
  biometric_include_auto: false,
  biometric_auto_amount: "",
  biometric_auto_renewal: "",
  biometric_include_manual: false,
  biometric_manual_amount: "",
  biometric_manual_renewal: "",
};

function QuotationPlanAdd(props) {
  const location = useLocation();
  const navigate = useNavigate();
  const [planId, setPlanId] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const [submitDisable, setSubmitDisable] = React.useState(false);
  const [formData, setFormData] = React.useState(emptyForm);
  const [serviceToggles, setServiceToggles] = React.useState(emptyToggles);
  const [ERPFeatureList, setERPFeatureList] = React.useState([]);
  const [selectedErpFeatures, setSelectedErpFeatures] = React.useState([]);
  const [digitalOptions, setDigitalOptions] = React.useState([]);
  const [selectedDigitalFeatures, setSelectedDigitalFeatures] = React.useState([]);
  const [digitalFeatureSelection, setDigitalFeatureSelection] = React.useState({});
  const [digitalLoading, setDigitalLoading] = React.useState(false);
  const [errors, setErrors] = React.useState({});

  const isEditMode = !!planId;

  const erpCategoriesEntries = React.useMemo(() => {
    const grouped = (ERPFeatureList || []).reduce((acc, item) => {
      const cat = (item.category || "").trim() || "General";
      if (!acc[cat]) acc[cat] = [];
      acc[cat].push(item);
      return acc;
    }, {});
    return Object.entries(grouped).sort(([a], [b]) => a.localeCompare(b));
  }, [ERPFeatureList]);

  const metaById = React.useMemo(() => {
    const m = new Map();
    (digitalOptions || []).forEach((f) => m.set(String(f.id), f));
    return m;
  }, [digitalOptions]);

  const allErpIds = React.useMemo(
    () => (ERPFeatureList || []).map((f) => String(f.id)),
    [ERPFeatureList]
  );

  const toggleAllErpModules = (checked) => {
    setSelectedErpFeatures(checked ? [...allErpIds] : []);
  };

  const toggleCategoryErpModules = (items) => (checked) => {
    const catIds = items.map((f) => String(f.id));
    setSelectedErpFeatures((prev) => {
      const set = new Set(prev);
      if (checked) {
        catIds.forEach((id) => set.add(id));
      } else {
        catIds.forEach((id) => set.delete(id));
      }
      return Array.from(set);
    });
  };

  React.useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    setPlanId(searchParams.get("id"));
  }, [location.search]);

  React.useEffect(() => {
    getERPFeatureList();
    getDigitalMarketingFeatureList();
  }, []);

  React.useEffect(() => {
    if (planId) getPlanDetails(planId);
  }, [planId]);

  const getERPFeatureList = () => {
    getRequest(GET_URL.erpfeature?.api, { limit: 500, pageno: 1 }, props).then((response) => {
      if (response?.status === 200) setERPFeatureList(response.data?.data || []);
    });
  };

  const getDigitalMarketingFeatureList = () => {
    setDigitalLoading(true);
    getRequest(GET_URL.digitalmarketingfeature?.api, { limit: 500, pageno: 1 }, props)
      .then((response) => {
        if (response?.status === 200) setDigitalOptions(response.data?.data || []);
      })
      .finally(() => setDigitalLoading(false));
  };

  const getPlanDetails = (id) => {
    setLoading(true);
    getRequest(`${GET_URL.quotation_plans.api}${id}/`, {}, props)
      .then((response) => {
        if (response?.status !== 200) return;
        const plan = response.data?.data || response.data || {};
        setFormData({
          name: plan.name || "",
          description: plan.description || "",
          amount_per_student: plan.amount_per_student != null ? String(plan.amount_per_student) : "",
          erp_with_gst: !!plan.erp_with_gst,
          erp_gst_percent: plan.erp_gst_percent != null ? String(plan.erp_gst_percent) : "18",
          per_id_amount: plan.per_id_amount != null ? String(plan.per_id_amount) : "",
          biometric_include_auto: !!plan.biometric_include_auto,
          biometric_auto_amount: plan.biometric_auto_amount != null ? String(plan.biometric_auto_amount) : "",
          biometric_auto_renewal: plan.biometric_auto_renewal != null ? String(plan.biometric_auto_renewal) : "",
          biometric_include_manual: !!plan.biometric_include_manual,
          biometric_manual_amount: plan.biometric_manual_amount != null ? String(plan.biometric_manual_amount) : "",
          biometric_manual_renewal: plan.biometric_manual_renewal != null ? String(plan.biometric_manual_renewal) : "",
        });
        setServiceToggles({ ...emptyToggles, ...(plan.service_toggles || {}) });
        setSelectedErpFeatures(Array.isArray(plan.erp_features) ? plan.erp_features.map(String) : []);
        setDigitalFeatureSelection(plan.digital_features || {});
        if (Array.isArray(plan.digital_feature_details)) {
          setSelectedDigitalFeatures(plan.digital_feature_details);
          setDigitalOptions((prev) => mergeById(prev, plan.digital_feature_details));
        }
      })
      .finally(() => setLoading(false));
  };

  const mergeById = (base, incoming) => {
    const next = [...(base || [])];
    const seen = new Set(next.map((item) => String(item.id)));
    (incoming || []).forEach((item) => {
      if (!seen.has(String(item.id))) {
        seen.add(String(item.id));
        next.push(item);
      }
    });
    return next;
  };

  const updateField = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: "" }));
  };

  const handleToggle = (key) => (event) => {
    const checked = event.target.checked;
    setServiceToggles((prev) => ({ ...prev, [key]: checked }));
    if (key === "erp" && !checked) setSelectedErpFeatures([]);
    if (key === "otherService" && !checked) {
      setSelectedDigitalFeatures([]);
      setDigitalFeatureSelection({});
    }
    if (key === "idCard" && !checked) updateField("per_id_amount", "");
    if (key === "biometric" && !checked) {
      setFormData((prev) => ({
        ...prev,
        biometric_include_auto: false,
        biometric_auto_amount: "",
        biometric_auto_renewal: "",
        biometric_include_manual: false,
        biometric_manual_amount: "",
        biometric_manual_renewal: "",
      }));
    }
  };

  const onDigitalSelectionChange = (_, newFeatures) => {
    setSelectedDigitalFeatures(newFeatures || []);
    setDigitalFeatureSelection((prev) => {
      const next = {};
      const selectedIds = new Set((newFeatures || []).map((f) => String(f.id)));
      (newFeatures || []).forEach((feature) => {
        const idStr = String(feature.id);
        next[idStr] =
          prev[idStr] || {
            price: feature.price != null ? String(feature.price) : "",
            renewal_price: feature.renewal_price != null ? String(feature.renewal_price) : "",
            calculation_type: feature.calculation_type || "monthwise",
          };
      });
      Object.keys(prev).forEach((id) => {
        if (!selectedIds.has(id)) delete next[id];
      });
      return next;
    });
  };

  const setDigitalRow = (idStr, patch) => {
    setDigitalFeatureSelection((prev) => ({
      ...prev,
      [idStr]: { ...(prev[idStr] || {}), ...patch },
    }));
  };

  const validate = () => {
    const nextErrors = {};
    if (!String(formData.name || "").trim()) nextErrors.name = "Plan name is required";
    if (!serviceToggles.erp && !serviceToggles.otherService && !serviceToggles.idCard && !serviceToggles.biometric) {
      nextErrors.service = "Select at least one service";
    }
    if (serviceToggles.erp) {
      if (!selectedErpFeatures.length) nextErrors.erp_features = "Select at least one ERP module";
      if (!formData.amount_per_student || Number(formData.amount_per_student) <= 0) {
        nextErrors.amount_per_student = "Enter valid amount per student";
      }
      if (formData.erp_with_gst && String(formData.erp_gst_percent || "").trim() === "") {
        nextErrors.erp_gst_percent = "GST percent is required";
      }
    }
    if (serviceToggles.otherService && !Object.keys(digitalFeatureSelection).length) {
      nextErrors.digital_features = "Select at least one other service";
    }
    if (serviceToggles.idCard && (!formData.per_id_amount || Number(formData.per_id_amount) <= 0)) {
      nextErrors.per_id_amount = "Enter valid ID card amount";
    }
    if (serviceToggles.biometric) {
      if (
        formData.biometric_include_auto &&
        (!formData.biometric_auto_amount || Number(formData.biometric_auto_amount) <= 0)
      ) {
        nextErrors.biometric_auto_amount = "Enter valid auto amount";
      }
      if (
        formData.biometric_include_manual &&
        (!formData.biometric_manual_amount || Number(formData.biometric_manual_amount) <= 0)
      ) {
        nextErrors.biometric_manual_amount = "Enter valid manual amount";
      }
    }
    Object.entries(digitalFeatureSelection).forEach(([id, row]) => {
      const label = metaById.get(String(id))?.name || `Other service ${id}`;
      if (!row?.price || Number(row.price) <= 0) {
        nextErrors.digital_features = `${label}: enter amount`;
      }
    });
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const cleanDecimal = (value) => {
    const str = String(value ?? "").trim();
    return str === "" ? null : str;
  };

  const submit = () => {
    if (!validate()) return;
    setSubmitDisable(true);
    const payload = {
      name: String(formData.name || "").trim(),
      description: formData.description || "",
      service_toggles: serviceToggles,
      erp_features: selectedErpFeatures,
      digital_features: digitalFeatureSelection,
      amount_per_student: serviceToggles.erp ? cleanDecimal(formData.amount_per_student) : null,
      erp_with_gst: serviceToggles.erp ? !!formData.erp_with_gst : false,
      erp_gst_percent: serviceToggles.erp && formData.erp_with_gst ? cleanDecimal(formData.erp_gst_percent) : null,
      per_id_amount: serviceToggles.idCard ? cleanDecimal(formData.per_id_amount) : null,
      biometric_include_auto: serviceToggles.biometric ? !!formData.biometric_include_auto : false,
      biometric_auto_amount:
        serviceToggles.biometric && formData.biometric_include_auto ? cleanDecimal(formData.biometric_auto_amount) : null,
      biometric_auto_renewal:
        serviceToggles.biometric && formData.biometric_include_auto ? cleanDecimal(formData.biometric_auto_renewal) : null,
      biometric_include_manual: serviceToggles.biometric ? !!formData.biometric_include_manual : false,
      biometric_manual_amount:
        serviceToggles.biometric && formData.biometric_include_manual ? cleanDecimal(formData.biometric_manual_amount) : null,
      biometric_manual_renewal:
        serviceToggles.biometric && formData.biometric_include_manual ? cleanDecimal(formData.biometric_manual_renewal) : null,
    };

    const request = isEditMode
      ? putRequest(`${PUT_URL.quotation_plans.api}${planId}/`, payload, props)
      : postRequest(POST_URL.quotation_plans.api, payload, props);

    request
      .then((response) => {
        if (response?.status === 200) {
          Swal.fire({
            type: "success",
            title: "Success",
            text: `Quotation Plan ${isEditMode ? "updated" : "added"} successfully`,
          });
          navigate("/quotation/plan/list");
        }
      })
      .finally(() => setSubmitDisable(false));
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, width: "100%" }}>
      <Grid container alignItems="center" sx={{ mb: 2 }}>
        <Grid item xs={12} md={6}>
          <Typography variant="h5" fontWeight={700}>
            {isEditMode ? "Edit Quotation Plan" : "Add Quotation Plan"}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Save reusable defaults for quotation generation
          </Typography>
        </Grid>
        <Grid item xs={12} md={6} textAlign="right">
          <Button
            variant="outlined"
            startIcon={<VisibilityOutlined />}
            onClick={() => navigate("/quotation/plan/list")}
            sx={{ textTransform: "none" }}
          >
            View quotation plans
          </Button>
        </Grid>
      </Grid>

      <Paper variant="outlined" sx={{ p: { xs: 2, sm: 3 }, borderRadius: 2 }}>
        {loading ? (
          <Box sx={{ p: 4, textAlign: "center" }}>
            <CircularProgress size={26} />
          </Box>
        ) : (
          <>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Quotation plan name"
                  value={formData.name}
                  onChange={(e) => updateField("name", e.target.value)}
                  error={!!errors.name}
                  helperText={errors.name}
                  required
                  fullWidth
                  size="small"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Description"
                  value={formData.description}
                  onChange={(e) => updateField("description", e.target.value)}
                  fullWidth
                  size="small"
                />
              </Grid>
            </Grid>

            <Paper elevation={0} variant="outlined" sx={{ mt: 3, mb: 3, p: 2, borderRadius: 2, bgcolor: "grey.50" }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                Services to include
              </Typography>
              <Grid container spacing={1}>
                <Grid item xs={12} sm={6} md={3}>
                  <FormControlLabel
                    control={<Checkbox checked={serviceToggles.erp} onChange={handleToggle("erp")} />}
                    label="ERP"
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <FormControlLabel
                    control={<Checkbox checked={serviceToggles.otherService} onChange={handleToggle("otherService")} />}
                    label="Other service"
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <FormControlLabel
                    control={<Checkbox checked={serviceToggles.idCard} onChange={handleToggle("idCard")} />}
                    label="ID card"
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <FormControlLabel
                    control={<Checkbox checked={serviceToggles.biometric} onChange={handleToggle("biometric")} />}
                    label="Biometric"
                  />
                </Grid>
              </Grid>
              {errors.service && (
                <Typography variant="caption" color="error">
                  {errors.service}
                </Typography>
              )}
            </Paper>

            {serviceToggles.erp && (
              <>
                <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>
                  ERP pricing and modules
                </Typography>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} md={4}>
                    <TextField
                      label="Amount per student"
                      type="number"
                      size="small"
                      fullWidth
                      value={formData.amount_per_student}
                      onChange={(e) => updateField("amount_per_student", e.target.value)}
                      error={!!errors.amount_per_student}
                      helperText={errors.amount_per_student}
                      inputProps={{ min: 0.01, step: 0.01 }}
                      required
                    />
                  </Grid>
                  <Grid item xs={12} md={8}>
                    <ToggleButtonGroup
                      exclusive
                      size="small"
                      value={formData.erp_with_gst ? "with" : "without"}
                      onChange={(_, value) => {
                        if (value != null) updateField("erp_with_gst", value === "with");
                      }}
                    >
                      <ToggleButton value="without" sx={{ textTransform: "none", px: 2 }}>
                        Without GST
                      </ToggleButton>
                      <ToggleButton value="with" sx={{ textTransform: "none", px: 2 }}>
                        With GST
                      </ToggleButton>
                    </ToggleButtonGroup>
                  </Grid>
                  {formData.erp_with_gst && (
                    <Grid item xs={12} md={4}>
                      <TextField
                        label="GST %"
                        type="number"
                        size="small"
                        fullWidth
                        value={formData.erp_gst_percent}
                        onChange={(e) => updateField("erp_gst_percent", e.target.value)}
                        error={!!errors.erp_gst_percent}
                        helperText={errors.erp_gst_percent}
                        inputProps={{ min: 0, max: 100, step: 0.01 }}
                        required
                      />
                    </Grid>
                  )}
                </Grid>

                <Box mt={3} mb={2}>
                  <Divider />
                </Box>
                {erpCategoriesEntries.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    Loading modules or none available.
                  </Typography>
                ) : (
                  <>
                    <FormControlLabel
                      sx={{ mb: 1, ml: 0 }}
                      control={
                        <Checkbox
                          size="small"
                          checked={
                            allErpIds.length > 0 && allErpIds.every((id) => selectedErpFeatures.includes(id))
                          }
                          indeterminate={
                            allErpIds.length > 0 &&
                            selectedErpFeatures.some((id) => allErpIds.includes(id)) &&
                            !allErpIds.every((id) => selectedErpFeatures.includes(id))
                          }
                          onChange={(e) => toggleAllErpModules(e.target.checked)}
                        />
                      }
                      label={<Typography variant="body2">Select all modules at once</Typography>}
                    />
                    <Grid container spacing={2}>
                    {erpCategoriesEntries.map(([category, items]) => {
                      const catIds = items.map((f) => String(f.id));
                      const allCatSelected =
                        catIds.length > 0 && catIds.every((id) => selectedErpFeatures.includes(id));
                      const someCatSelected =
                        catIds.some((id) => selectedErpFeatures.includes(id)) && !allCatSelected;
                      return (
                      <Grid item xs={12} md={4} key={category}>
                        <Paper variant="outlined" sx={{ p: 1.5, height: "100%", borderRadius: 2 }}>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                              gap: 1,
                              mb: 1,
                            }}
                          >
                            <Typography variant="subtitle2" fontWeight={700} sx={{ flex: 1, minWidth: 0 }}>
                              {category}
                            </Typography>
                            <FormControlLabel
                              sx={{ mr: 0, flexShrink: 0 }}
                              control={
                                <Checkbox
                                  size="small"
                                  checked={allCatSelected}
                                  indeterminate={someCatSelected}
                                  onChange={(e) =>
                                    toggleCategoryErpModules(items)(e.target.checked)
                                  }
                                />
                              }
                              label={
                                <Typography variant="caption" noWrap>
                                  Whole module
                                </Typography>
                              }
                            />
                          </Box>
                          {items.map((feature) => {
                            const idStr = String(feature.id);
                            return (
                              <FormControlLabel
                                key={feature.id}
                                sx={{ display: "block", ml: 0 }}
                                control={
                                  <Checkbox
                                    size="small"
                                    checked={selectedErpFeatures.includes(idStr)}
                                    onChange={(ev) => {
                                      if (ev.target.checked) {
                                        setSelectedErpFeatures((prev) => (prev.includes(idStr) ? prev : [...prev, idStr]));
                                      } else {
                                        setSelectedErpFeatures((prev) => prev.filter((x) => x !== idStr));
                                      }
                                    }}
                                  />
                                }
                                label={<Typography variant="body2">{feature.name}</Typography>}
                              />
                            );
                          })}
                        </Paper>
                      </Grid>
                      );
                    })}
                    </Grid>
                  </>
                )}
                {errors.erp_features && (
                  <Typography variant="caption" color="error">
                    {errors.erp_features}
                  </Typography>
                )}
              </>
            )}

            {serviceToggles.otherService && (
              <>
                <Box mt={3} mb={2}>
                  <Divider />
                </Box>
                <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>
                  Other services
                </Typography>
                <Autocomplete
                  multiple
                  disableCloseOnSelect
                  loading={digitalLoading}
                  options={digitalOptions}
                  value={selectedDigitalFeatures}
                  onChange={onDigitalSelectionChange}
                  getOptionLabel={(option) => option?.name || ""}
                  isOptionEqualToValue={(a, b) => String(a?.id) === String(b?.id)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Select other services"
                      size="small"
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {digitalLoading ? <CircularProgress color="inherit" size={18} sx={{ mr: 1 }} /> : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                />
                {Object.keys(digitalFeatureSelection).map((fid) => {
                  const row = digitalFeatureSelection[fid] || {};
                  const label = metaById.get(String(fid))?.name || fid;
                  return (
                    <Paper key={fid} variant="outlined" sx={{ p: 2, mt: 2, borderRadius: 2 }}>
                      <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.5 }}>
                        {label}
                      </Typography>
                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={4}>
                          <TextField
                            label="Amount"
                            type="number"
                            size="small"
                            fullWidth
                            required
                            value={row.price ?? ""}
                            onChange={(e) => setDigitalRow(fid, { price: e.target.value })}
                            inputProps={{ min: 0.01, step: 0.01 }}
                          />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                          <TextField
                            label="Renewal amount"
                            type="number"
                            size="small"
                            fullWidth
                            value={row.renewal_price ?? ""}
                            onChange={(e) => setDigitalRow(fid, { renewal_price: e.target.value })}
                            inputProps={{ min: 0, step: 0.01 }}
                          />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                          <TextField
                            select
                            label="Pricing basis"
                            size="small"
                            fullWidth
                            value={row.calculation_type || "monthwise"}
                            onChange={(e) => setDigitalRow(fid, { calculation_type: e.target.value })}
                          >
                            <MenuItem value="monthwise">Month wise</MenuItem>
                            <MenuItem value="yearwise">Year wise</MenuItem>
                            <MenuItem value="studentcount">Student count</MenuItem>
                          </TextField>
                        </Grid>
                      </Grid>
                    </Paper>
                  );
                })}
                {errors.digital_features && (
                  <Typography variant="caption" color="error">
                    {errors.digital_features}
                  </Typography>
                )}
              </>
            )}

            {serviceToggles.idCard && (
              <>
                <Box mt={3} mb={2}>
                  <Divider />
                </Box>
                <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>
                  ID card
                </Typography>
                <TextField
                  label="ID card amount"
                  type="number"
                  size="small"
                  value={formData.per_id_amount}
                  onChange={(e) => updateField("per_id_amount", e.target.value)}
                  error={!!errors.per_id_amount}
                  helperText={errors.per_id_amount}
                  inputProps={{ min: 0.01, step: 0.01 }}
                  required
                />
              </>
            )}

            {serviceToggles.biometric && (
              <>
                <Box mt={3} mb={2}>
                  <Divider />
                </Box>
                <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>
                  Biometric
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={formData.biometric_include_auto}
                            onChange={(e) => updateField("biometric_include_auto", e.target.checked)}
                          />
                        }
                        label="Auto"
                      />
                      {formData.biometric_include_auto && (
                        <Grid container spacing={2}>
                          <Grid item xs={12} sm={6}>
                            <TextField
                              label="Amount"
                              type="number"
                              size="small"
                              fullWidth
                              value={formData.biometric_auto_amount}
                              onChange={(e) => updateField("biometric_auto_amount", e.target.value)}
                              error={!!errors.biometric_auto_amount}
                              helperText={errors.biometric_auto_amount}
                              required
                            />
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <TextField
                              label="Renewal amount"
                              type="number"
                              size="small"
                              fullWidth
                              value={formData.biometric_auto_renewal}
                              onChange={(e) => updateField("biometric_auto_renewal", e.target.value)}
                            />
                          </Grid>
                        </Grid>
                      )}
                    </Paper>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={formData.biometric_include_manual}
                            onChange={(e) => updateField("biometric_include_manual", e.target.checked)}
                          />
                        }
                        label="Manual"
                      />
                      {formData.biometric_include_manual && (
                        <Grid container spacing={2}>
                          <Grid item xs={12} sm={6}>
                            <TextField
                              label="Amount"
                              type="number"
                              size="small"
                              fullWidth
                              value={formData.biometric_manual_amount}
                              onChange={(e) => updateField("biometric_manual_amount", e.target.value)}
                              error={!!errors.biometric_manual_amount}
                              helperText={errors.biometric_manual_amount}
                              required
                            />
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <TextField
                              label="Renewal amount"
                              type="number"
                              size="small"
                              fullWidth
                              value={formData.biometric_manual_renewal}
                              onChange={(e) => updateField("biometric_manual_renewal", e.target.value)}
                            />
                          </Grid>
                        </Grid>
                      )}
                    </Paper>
                  </Grid>
                </Grid>
                {errors.biometric && (
                  <Typography variant="caption" color="error">
                    {errors.biometric}
                  </Typography>
                )}
              </>
            )}

            <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, mt: 3 }}>
              <Button variant="outlined" onClick={() => navigate("/quotation/plan/list")}>
                Cancel
              </Button>
              <Button variant="contained" disabled={submitDisable} onClick={submit}>
                {submitDisable ? "Saving..." : "Save"}
              </Button>
            </Box>
          </>
        )}
      </Paper>
    </Box>
  );
}

export default QuotationPlanAdd;
